import React, { useEffect, useRef, useState } from "react";
import { CheckCircle2, Loader2, Mail } from "lucide-react";
import { fmtMoney, formatErr } from "../../lib/api";
import { invoiceBalanceDue } from "../../lib/invoiceResendPolicy";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";

export const InvoiceResendDialog = ({ invoice, onOpenChange, onSend }) => {
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const inFlight = useRef(false);
  useEffect(() => { setError(""); }, [invoice?.id]);

  const send = async () => {
    if (!invoice || inFlight.current) return;
    inFlight.current = true;
    setSending(true);
    setError("");
    try {
      const result = await onSend(invoice);
      if (result?.sent) onOpenChange(false);
      else if (!result?.needsEmail) setError(result?.error || "The email was not sent. Your changes are saved.");
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message || "The email was not sent. Your changes are saved.");
    } finally {
      inFlight.current = false;
      setSending(false);
    }
  };

  if (!invoice) return null;
  return (
    <Dialog open onOpenChange={(open) => { if (!inFlight.current) onOpenChange(open); }}>
      <DialogContent className="max-w-md" data-testid="invoice-resend-dialog" closeTestId="invoice-resend-close">
        <DialogHeader>
          <div className="mb-2 flex items-center gap-2 text-sm font-medium text-emerald-700" data-testid="invoice-resend-saved"><CheckCircle2 className="h-4 w-4" /> Changes saved</div>
          <DialogTitle data-testid="invoice-resend-title">Resend updated invoice?</DialogTitle>
          <DialogDescription data-testid="invoice-resend-description">
            Invoice {invoice.number} was already sent and still has a balance due. Email the updated copy to {invoice.colony_name || "the colony"}?
          </DialogDescription>
        </DialogHeader>
        <dl className="grid grid-cols-2 gap-4 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="invoice-resend-summary">
          <div><dt className="text-xs text-muted-foreground" data-testid="invoice-resend-total-label">Updated total</dt><dd className="mt-1 break-words font-semibold mono-num" data-testid="invoice-resend-total">{fmtMoney(invoice.total)}</dd></div>
          <div><dt className="text-xs text-muted-foreground" data-testid="invoice-resend-balance-label">Balance due</dt><dd className="mt-1 break-words font-semibold mono-num text-rose-800" data-testid="invoice-resend-balance">{fmtMoney(invoiceBalanceDue(invoice))}</dd></div>
        </dl>
        {error && <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 break-words" data-testid="invoice-resend-error">{error}<p className="mt-1">Your invoice changes are still saved.</p></div>}
        <DialogFooter>
          <Button variant="outline" disabled={sending} onClick={() => onOpenChange(false)} data-testid="invoice-resend-not-now">Not now</Button>
          <Button disabled={sending} onClick={send} data-testid="invoice-resend-send">{sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}{sending ? "Sending…" : "Send email"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};